import { useLayoutEffect, useRef } from "react";

// Wisselt er iemand van plek in een ranglijst, dan hoort die verkoper naar de
// nieuwe plek te schuiven in plaats van te verspringen. Dat kan CSS niet zelf:
// een rij die in de DOM van positie wisselt, staat meteen op de eindplek.
//
// Daarom de FLIP-truc: meet waar elke rij stond (First), meet waar hij nu staat
// (Last), zet hem met een transform terug op de oude plek (Invert) en laat die
// transform naar nul lopen (Play). De layout is dus al klaar; alleen het beeld
// haalt het nog even in.
//
// Voorwaarden: elke rij heeft een data-flip-key met de identiteit van de
// verkoper (niet de plek), en in React staat op diezelfde rij dezelfde key -
// anders hergebruikt React het element voor een ándere verkoper en verschiet de
// tekst alsnog.
const ease = "cubic-bezier(0.22, 1, 0.36, 1)";
const duration = 620;

const useFlipReorder = (containerRef, orderKey) => {
  // De layout van de vorige beurt; de eerste keer is die leeg, dus dan beweegt
  // er niets - een lijst die net in beeld komt hoort niet te schuiven.
  const previous = useRef(new Map());

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const nodes = Array.from(container.querySelectorAll("[data-flip-key]"));

    // Eerst alleen meten: de zichtbare plek van nu. Loopt er nog een beweging,
    // dan zit daar een transform op en is dit dus de plek halverwege - precies
    // waar de nieuwe beweging moet beginnen.
    const entries = nodes.map((node) => ({
      node,
      key: node.dataset.flipKey,
      interrupted: node.style.transform !== "",
      visual: node.getBoundingClientRect(),
    }));

    // Dan de transforms eraf, zonder transition, om de kale eindplek te meten.
    entries.forEach(({ node }) => {
      node.style.transition = "none";
      node.style.transform = "";
    });

    const snapshot = new Map();
    const moved = [];

    entries.forEach((entry) => {
      const layout = entry.node.getBoundingClientRect();
      snapshot.set(entry.key, layout);

      const start = entry.interrupted ? entry.visual : previous.current.get(entry.key);
      if (!start) return;

      const dx = start.left - layout.left;
      const dy = start.top - layout.top;
      // Onder een pixel is het afrondingsruis, geen verhuizing.
      if (Math.abs(dx) < 1 && Math.abs(dy) < 1) return;

      moved.push({ node: entry.node, dx, dy });
    });

    previous.current = snapshot;

    const movedNodes = new Set(moved.map(({ node }) => node));
    entries.forEach(({ node }) => {
      // Wie blijft staan krijgt zijn eigen CSS-transitions terug.
      if (!movedNodes.has(node)) node.style.transition = "";
    });

    if (!moved.length) return;

    moved.forEach(({ node, dx, dy }) => {
      node.style.transform = `translate(${dx}px, ${dy}px)`;
    });

    // De browser dwingen de startpositie echt te verwerken; zonder deze
    // meting slaat hij de transform samen met het opruimen eronder plat en
    // zie je weer een sprong.
    void container.offsetWidth;

    moved.forEach(({ node }) => {
      node.style.transition = `transform ${duration}ms ${ease}`;
      node.style.transform = "";
    });
  }, [containerRef, orderKey]);
};

export default useFlipReorder;
